"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePlotArea, useXAxisInverseDataSnapScale, useXAxisScale } from 'recharts'
import type { MouseHandlerDataParam, SyncMethod } from 'recharts/types/synchronisation/types'
import { fmtMsDate } from '@/lib/finance/format'
import { usePersistedState } from '@/lib/finance/usePersistedState'

/**
 * One cursor for a page of time charts.
 *
 * Every chart on the Net Worth page plots the same x — epoch ms in `t` — but
 * samples it differently (the hero line and the stacked cards each downsample
 * their own rows), so recharts' default index-based tooltip sync would point
 * at different dates in different charts. `syncByNearestTime` matches by
 * VALUE instead: hovering Sep 12 in one chart lights up the nearest point to
 * Sep 12 in every other chart with the same `syncId`.
 *
 * On top of the shared hover there is one PINNED date per page: click a date
 * to pin it, click another date to move the pin, click the chart outside the
 * plot area (axis labels, margins) to clear it. The pin is a dashed line with
 * its date on every chart plus the tooltip parked there (recharts'
 * `defaultIndex`), so the values at the pinned date stay readable while the
 * mouse wanders; hovering draws the ordinary solid crosshair alongside it.
 *
 * The whole thing is OPT-IN — five crosshairs and five tooltips lighting up on
 * every mouse move is busy by default. The "Link charts" toggle (persisted per
 * browser, off until switched on) enables both the sync and the pin; off, each
 * chart's tooltip is its own and clicks do nothing.
 *
 * Usage, per chart:
 *   const cursor = useChartCursor()          // once, in the page
 *   <LinkedCursorToggle cursor={cursor} />   // once, wherever the page wants it
 *   <AreaChart key={cursor.resetKey} {...cursor.chartProps} …>   // sync when linked
 *     <Tooltip defaultIndex={pinnedIndex(data, cursor.pinnedT)} … />
 *     <ChartCursorLayer cursor={cursor} />
 *
 * WHY THE LAYER OWNS THE CLICK. recharts' chart `onClick` hands over a
 * tooltip state that is one click STALE in 3.x (a click also toggles the
 * click-trigger tooltip and the handler sees the pre-toggle state), and the
 * hover label read from the store can lag a fast move-and-click. And an SVG
 * click target under the series never sees a click that lands on a stacked
 * band (recharts draws graphical items above other children). So the layer
 * listens on the chart's wrapper element itself and resolves every click by
 * geometry: inside the plot area's box → pin the date the click's x maps to
 * through the axis' inverse data-snap scale; anywhere else in the chart (axis
 * labels, margins) → clear. No interaction state is involved.
 */

export const CHART_SYNC_ID = 'finance-time'

/** Pick the tick whose x value is closest to the active label of the chart
 * being hovered. Ticks arrive in x order, so a binary search would do, but a
 * chart holds at most MAX_CHART_POINTS (480) ticks — a linear scan per mouse
 * move is nothing next to the SVG work recharts does for the same event. */
export const syncByNearestTime: SyncMethod = (ticks, data: MouseHandlerDataParam) => {
  const target = Number(data.activeLabel)
  if (!Number.isFinite(target) || ticks.length === 0) return -1
  let best = 0
  let bestDist = Math.abs(Number(ticks[0].value) - target)
  for (let i = 1; i < ticks.length; i++) {
    const d = Math.abs(Number(ticks[i].value) - target)
    if (d < bestDist) {
      best = i
      bestDist = d
    }
  }
  return ticks[best].index
}

/** Index of the row closest in time to `t`, for `<Tooltip defaultIndex>`;
 * undefined when nothing is pinned (the tooltip then follows the mouse only). */
export function pinnedIndex(rows: readonly { t?: unknown }[], t: number | null): number | undefined {
  if (t == null || rows.length === 0) return undefined
  // A pin outside this chart's window (after a range change) parks nothing:
  // snapping it to the edge row would read as a value at the wrong date.
  if (t < Number(rows[0].t) || t > Number(rows[rows.length - 1].t)) return undefined
  let best = 0
  let bestDist = Math.abs(Number(rows[0].t) - t)
  for (let i = 1; i < rows.length; i++) {
    const d = Math.abs(Number(rows[i].t) - t)
    if (d < bestDist) {
      best = i
      bestDist = d
    }
  }
  return best
}

export type ChartCursor = {
  /** Linked cursor on (the persisted toggle). Off: no sync, no pin. */
  enabled: boolean
  setEnabled: (on: boolean) => void
  /** The pinned date (epoch ms), or null. */
  pinnedT: number | null
  pin: (t: number) => void
  clear: () => void
  /** Put on every chart as its React `key`. Bumps whenever a pin is DROPPED
   * (cleared, or the toggle switched off), which remounts the charts.
   *
   * WHY: recharts keeps a `defaultIndex` tooltip on screen after the prop
   * goes away until the next mouse interaction on that chart — the parked
   * state was broadcast through the sync, and only a real hover-then-leave
   * broadcasts the all-clear — so a pin cleared from the axis area or by the
   * toggle left five tooltips showing a date nobody chose. Replaying the
   * interaction with synthetic mouse events raced across the five charts; a
   * remount is deterministic and, with the series' animations off, invisible. */
  resetKey: number
  /** Spread onto every chart that shares the cursor. */
  chartProps: {
    syncId: string
    syncMethod: SyncMethod
  }
}

export const LINKED_CURSOR_KEY = 'finance.linkedCursor'
/** Off = the sync stays wired but matches nothing, so every chart's tooltip is
 * its own. The syncId itself never changes: adding/removing it swaps recharts'
 * sync wiring under live charts, and a tooltip parked by `defaultIndex` then
 * stayed on screen after the pin was dropped. */
const syncNone: SyncMethod = () => -1
const LINKED_PROPS = { syncId: CHART_SYNC_ID, syncMethod: syncByNearestTime } as const
const UNLINKED_PROPS = { syncId: CHART_SYNC_ID, syncMethod: syncNone } as const

export function useChartCursor(): ChartCursor {
  const [enabled, setStored] = usePersistedState<boolean>(LINKED_CURSOR_KEY, false)
  const [pinnedT, setPinnedT] = useState<number | null>(null)
  const [resetKey, setResetKey] = useState(0)
  const pin = useCallback((t: number) => setPinnedT(t), [])
  // Dropping a pin remounts the charts (see resetKey); clearing nothing is a no-op.
  const drop = useCallback(() => {
    setPinnedT((prev) => {
      if (prev != null) setResetKey((k) => k + 1)
      return null
    })
  }, [])
  // Switching off also drops the pin — a dashed line nobody can clear (clicks
  // are inert while off) would be the one leftover of the linked mode.
  const setEnabled = useCallback((on: boolean) => {
    setStored(on)
    if (!on) drop()
  }, [setStored, drop])
  return useMemo(
    () => ({ enabled, setEnabled, pinnedT: enabled ? pinnedT : null, pin, clear: drop, resetKey, chartProps: enabled ? LINKED_PROPS : UNLINKED_PROPS }),
    [enabled, setEnabled, pinnedT, pin, drop, resetKey],
  )
}

/** The "Link charts" pill: on = hover + pin shared across the page's charts. */
export function LinkedCursorToggle({ cursor }: { cursor: ChartCursor }) {
  const { enabled, setEnabled } = cursor
  return (
    <button
      type="button"
      onClick={() => setEnabled(!enabled)}
      aria-pressed={enabled}
      data-linked-cursor={enabled ? 'on' : 'off'}
      title="Hover one chart to read the same date on all of them; click a date to pin it"
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
        enabled ? 'border-transparent bg-surface-3 text-ink' : 'border-edge text-ink-2 hover:bg-surface-3/60'
      }`}
    >
      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" className="shrink-0">
        <line x1="6" y1="1" x2="6" y2="11" stroke="currentColor" strokeWidth="1.5" strokeDasharray={enabled ? undefined : '2 2'} />
        <circle cx="6" cy="6" r="1.75" fill="currentColor" />
      </svg>
      Link charts
    </button>
  )
}

/** Render inside every chart that shares the cursor (last, so the pinned line
 * draws above the series). Owns the chart's click → pin/clear, and draws the
 * pinned date as a dashed line with its date at the top — nothing while the
 * cursor is off, no date is pinned, or the pinned date is outside this
 * chart's window. */
export function ChartCursorLayer({ cursor }: { cursor: ChartCursor }) {
  const plot = usePlotArea()
  const scale = useXAxisScale()
  const inverse = useXAxisInverseDataSnapScale()
  const { enabled, pinnedT: t, pin, clear } = cursor
  const boxRef = useRef<SVGRectElement>(null)

  useEffect(() => {
    const box = boxRef.current
    const svg = box?.ownerSVGElement
    const wrapper = svg?.closest('.recharts-wrapper') ?? svg?.parentElement
    if (!enabled || !box || !svg || !wrapper || !inverse) return
    const onClick = (e: Event) => {
      const { clientX, clientY } = e as globalThis.MouseEvent
      const r = box.getBoundingClientRect()
      const inside = clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom
      if (!inside) return clear()
      const snapped = Number(inverse(clientX - svg.getBoundingClientRect().left))
      if (Number.isFinite(snapped)) pin(snapped)
    }
    wrapper.addEventListener('click', onClick)
    return () => wrapper.removeEventListener('click', onClick)
  }, [enabled, inverse, pin, clear])

  if (!plot) return null
  const x = enabled && t != null && scale ? Number(scale(t)) : NaN
  const onPlot = Number.isFinite(x) && x >= plot.x && x <= plot.x + plot.width
  // Date label hugs the line on whichever side has room.
  const labelLeft = x - plot.x > 96
  return (
    <g className="chart-cursor-layer" pointerEvents="none">
      {/* Invisible: the plot-area box the click handler measures against
          (always mounted so the layer can find its chart; flagged only while
          the cursor is on, which is what the tests count). */}
      <rect ref={boxRef} data-plot-area={enabled ? '' : undefined} x={plot.x} y={plot.y} width={plot.width} height={plot.height} fill="none" />
      {onPlot && t != null && (
        <g className="pinned-cursor">
          <line x1={x} x2={x} y1={plot.y} y2={plot.y + plot.height} stroke="var(--text-primary)" strokeWidth={1} strokeDasharray="4 3" />
          <text
            x={labelLeft ? x - 5 : x + 5}
            y={plot.y + 11}
            textAnchor={labelLeft ? 'end' : 'start'}
            fill="var(--text-muted)"
            fontSize={11}
          >
            {fmtMsDate(String(t))}
          </text>
        </g>
      )}
    </g>
  )
}
